import { useEffect, useRef, useState } from "react";
import { X, Video } from "lucide-react";
import api from "../lib/api";

const JITSI_SCRIPT = "https://meet.jit.si/external_api.js";

function loadJitsi() {
  return new Promise((resolve, reject) => {
    if (window.JitsiMeetExternalAPI) return resolve();
    const existing = document.getElementById("jitsi-external-api");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = "jitsi-external-api";
    s.src = JITSI_SCRIPT;
    s.async = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

export function MeetingRoom({ meetingId, onClose }) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const [meeting, setMeeting] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/meetings/${meetingId}`);
        if (cancelled) return;
        setMeeting(data);
        await loadJitsi();
        if (cancelled || !containerRef.current || !window.JitsiMeetExternalAPI) return;
        apiRef.current = new window.JitsiMeetExternalAPI(data.jitsi_domain, {
          roomName: data.room,
          parentNode: containerRef.current,
          userInfo: { displayName: data.display_name },
          configOverwrite: {
            startWithAudioMuted: !data.is_host,
            prejoinPageEnabled: true,
            disableDeepLinking: true,
            toolbarButtons: ["microphone", "camera", "desktop", "chat", "raisehand", "tileview", "participants-pane", "settings", "recording", "fullscreen", "hangup"],
          },
          interfaceConfigOverwrite: { MOBILE_APP_PROMO: false },
        });
        apiRef.current.addEventListener("readyToClose", () => onClose && onClose());
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.detail || "Impossible de rejoindre la réunion.");
      }
    })();
    return () => {
      cancelled = true;
      try { apiRef.current && apiRef.current.dispose(); } catch { /* noop */ }
    };
  }, [meetingId, onClose]);

  return (
    <div data-testid="meeting-room-overlay" className="fixed inset-0 z-[100] bg-[#0b0d0c] flex flex-col">
      <div className="flex items-center justify-between px-4 h-14 bg-[#1A1A1A] text-white shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <Video className="w-5 h-5 text-[#D94F2B] shrink-0" />
          <span className="font-head font-bold truncate">{meeting?.title || "Mboa Meet"}</span>
        </div>
        <button data-testid="meeting-leave-btn" onClick={() => onClose && onClose()} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[#D32F2F] hover:bg-[#b71c1c] text-white text-sm font-semibold transition-colors">
          <X className="w-4 h-4" /> Quitter
        </button>
      </div>
      {error ? (
        <div data-testid="meeting-error" className="flex-1 flex items-center justify-center text-white/80 px-6 text-center">{error}</div>
      ) : (
        <div ref={containerRef} data-testid="meeting-jitsi-container" className="flex-1 min-h-0" />
      )}
    </div>
  );
}
